<template>
  <section id="home" class="hero">
    <div class="hero-background">
      <HeroNetwork />
    </div>

    <div class="container">
      <div class="hero-content">
        <h1 class="hero-main-title animate-fade-in" style="animation-delay: 0.1s">
          {{ $t('hero.title') }}
          <span class="hero-title-highlight">{{ $t('hero.titleHighlight') }}</span>
        </h1>
        <p class="hero-subtitle animate-fade-in" style="animation-delay: 0.25s">
          {{ $t('hero.subtitle') }}
        </p>
        <div class="hero-cta animate-fade-in" style="animation-delay: 0.4s">
          <a href="#clients" class="btn btn-primary">{{ $t('hero.ctaClients') }}</a>
          <a href="#contact" class="btn btn-outline">{{ $t('hero.ctaContact') }}</a>
        </div>

        <div class="hero-tech-stack animate-fade-in" style="animation-delay: 0.55s">
          <div v-for="tech in techStack" :key="tech.name" class="tech-chip">
            <img :src="tech.icon" :alt="tech.name" loading="lazy" />
            <span>{{ tech.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="scroll-indicator">
      <div class="scroll-arrow"></div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import HeroNetwork from './HeroNetwork.vue'

const techStack = [
  { name: 'React', icon: '/images/tech/react.svg' },
  { name: 'Vue.js', icon: '/images/tech/vuedotjs.svg' },
  { name: 'NestJS', icon: '/images/tech/nestjs.svg' },
  { name: 'PostgreSQL', icon: '/images/tech/postgresql.svg' },
  { name: 'Docker', icon: '/images/tech/docker.svg' },
  { name: 'Linux', icon: '/images/tech/linux.svg' },
  { name: 'pfSense', icon: '/images/tech/pfsense.svg' }
]

onMounted(() => {
  document.querySelector('.scroll-indicator')?.addEventListener('click', () => {
    document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' })
  })
})
</script>

<style scoped>
.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: var(--fixed-header-offset);
  background: var(--background-accent);
}

.hero-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 1;
  background: radial-gradient(ellipse at center, rgba(30, 58, 138, 0.25) 0%, transparent 70%);
}

.hero-content {
  max-width: 780px;
  margin: 0 auto;
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 80vh;
  justify-content: center;
  gap: var(--space-8);
  padding: var(--space-8) var(--space-6);
}

.hero-main-title {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(2.5rem, 5vw, var(--font-size-6xl));
  font-weight: var(--font-weight-extrabold);
  line-height: var(--line-height-tight);
  margin: 0;
  letter-spacing: -0.03em;
  color: var(--text-light);
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.45);
}

.hero-title-highlight {
  display: block;
  background: var(--gradient-primary);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite;
  margin-top: var(--space-2);
}

.hero-subtitle {
  font-size: var(--font-size-xl);
  color: rgba(255, 255, 255, 0.85);
  line-height: var(--line-height-relaxed);
  margin: 0;
  max-width: 560px;
  font-weight: var(--font-weight-medium);
}

.hero-cta {
  display: flex;
  gap: var(--space-4);
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.hero-cta .btn {
  padding: var(--space-4) var(--space-8);
  border-radius: var(--border-radius-full);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.hero-cta .btn-primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: var(--shadow-lg);
  border: none;
}

.hero-cta .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

.hero-cta .btn-outline {
  background: transparent;
  color: var(--text-light);
  border: 2px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.hero-cta .btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--primary-400);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.hero-tech-stack {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-6);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  width: 100%;
}

.tech-chip {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--border-radius-full);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tech-chip:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.18);
  transform: translateY(-2px);
}

.tech-chip img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.85;
}

.tech-chip span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.02em;
}

.scroll-indicator {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  animation: bounce 2s infinite;
}

.scroll-arrow {
  width: 30px;
  height: 30px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  40% {
    transform: translateX(-50%) translateY(-10px);
  }
  60% {
    transform: translateX(-50%) translateY(-5px);
  }
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.7s ease-out forwards;
  opacity: 0;
}

@media (max-width: 768px) {
  .hero-content {
    padding: var(--space-6) var(--space-4);
    gap: var(--space-6);
  }

  .hero-cta {
    flex-direction: column;
    gap: var(--space-3);
  }

  .hero-cta .btn {
    width: 100%;
    max-width: 280px;
  }

  .tech-chip {
    padding: var(--space-1) var(--space-3);
  }

  .tech-chip span {
    font-size: var(--font-size-xs);
  }
}
</style>
